'use client'

import {
  BalanceDataGridFilter,
  ClaimsStatusFilter,
  DateDataGridFilter,
  SingleSelectDataGridFilter,
  TextFieldDataGridFilter,
  ClaimRecipientFilter
} from '@repo/clients-matters-tables/DataGridTableCustomFilters'
import { ChatIcon } from '@repo/ui/ChatIcon'
import { WarningIcon } from '@repo/ui/WarningIcon'
import { ClaimsMoreMenuCard } from '@repo/claims-tables/ClaimsMoreMenuCard'
import { ClaimStatusDropdownButton } from '@repo/ui/ClaimStatusDropdownButton'
import { ClaimsToolbarModule } from '@repo/claims-tables/ClaimsToolbarModule'
import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import {
  DataGridPro,
  gridClasses,
  GridColDef,
  gridColumnDefinitionsSelector,
  GridColumnMenu,
  GridColumnMenuProps,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridRowId,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef
} from '@mui/x-data-grid-pro'
import { DataGridSelectionTray } from '@repo/ui/DataGridSelectionTray'
import { formatCurrency } from '@repo/utils/numbers'
import { convertDateToLocalTimeInIsoFormat, formatDate } from '@repo/utils/time'
import { PaperClipIcon } from '@repo/ui/PaperClipIcon'
import { Pill } from '@repo/ui/Pill'
import { PostingDateDialog } from '@repo/ui/PostingDateDialog'
import { TableClearFilters } from '@repo/ui/TableClearFilters'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClaimsStore } from '@repo/store/useClaimsStore'
import { useCommentsStore } from '@repo/store/useCommentsStore'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useExpensesStore } from '@repo/store/useExpensesStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { useUserPreferencesStore } from '@repo/store/useUserPreferencesStore'
import { useWindowSize } from '@uidotdev/usehooks'
import { v4 as uuidv4 } from 'uuid'
import {
  getDisabledStatusesFromPermissions,
  getDisabledStatusesFromCurrentStatuses,
  mergeDisabledStatuses
} from '@repo/utils/claimPermissions'
import defaultExpensesTableConfig from '@repo/store/defaultExpensesTableConfig'
import MUIButton from '@mui/material/Button'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

const parseMaybeJson = (val: unknown) => {
  if (!val) return {}
  if (typeof val === 'string') {
    try {
      return JSON.parse(val)
    } catch {
      return {}
    }
  }
  if (typeof val === 'object') return val as Record<string, any>
  return {}
}

export const ExpensesTableModule = ({ isSubmissionVersion }: ExpensesTableModuleProps) => {
  const apiRef = useGridApiRef()
  const navigate = useNavigate()

  const { allowedActions } = useMenuItemsStore()
  const { setExpenseClaimPermissions, submitExpenseClaims, processExpenseClaims } = allowedActions

  const theme = useTheme()

  const { userId } = useAuthenticationStore()
  const {
    isClaimsDrawerOpen,
    openNewClaimDrawer,
    setIsClaimsDrawerOpen,
    fetchAllExpenseUserPermissionsByProcessorUserId
  } = useClaimsStore()
  const { fetchAllExpensesClaimComments, commentsUpdate } = useCommentsStore()
  const { fetchAllExtendedExpenses, fetchAllExtendedExpensesAsCSV, updateExpenseClaimStatuses, expensesTableUpdate, expenseClaimPeriodStart, fetchExpenseClaimPeriodStart } =
    useExpensesStore()

  const {
    setExpensesTableFilters,
    setExpensesTableFiltersFromPreferences,
    expensesTableFilters,
    resetExpensesTableFilters,
    expensesTableFilterCount
  } = useDataGridFilterStore()
  const { pageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { 'Expenses and Overtime': expensesPreferences, updateUserPreference } = useUserPreferencesStore()

  const { height: windowHeight } = useWindowSize()
  const [tableHeight, setTableHeight] = useState(windowHeight ? windowHeight - 216 : 0)
  const [filtersReady, setFiltersReady] = useState(false)
  const [claimStatus, setClaimStatus] = useState<number | null>(null)
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({})
  const [expenses, setExpenses] = useState([])
  const [permissions, setPermissions] = useState<any[]>([])
  const [permissionsLoading, setPermissionsLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [postingDateDialogOpen, setPostingDateDialogOpen] = useState(false)

  const activePref = useMemo(
    () => expensesPreferences?.ExpenseClaim_GetAllExtendedBySearchCriteria,
    [expensesPreferences]
  )

  const resolvedConfig = useMemo(() => {
    const rv = activePref?.resolvedValue ?? activePref?.userValue ?? activePref?.defaultValue ?? {}
    return parseMaybeJson(rv)
  }, [activePref])

  const activePrefId = activePref?.userConfigurationTypeId ?? null

  const fallback = defaultExpensesTableConfig || {}

  const [loading, setLoading] = useState(false)
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({})
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>({ left: [], right: [] })
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([])
  const [selectedRowsById, setSelectedRowsById] = useState<Record<string, ExpenseRow>>({})
  const [totalRowCount, setTotalRowCount] = useState(0)

  const selectedRows = useMemo(() => Object.values(selectedRowsById), [selectedRowsById])

  useEffect(() => {
    if (!userId || isSubmissionVersion) {
      setPermissionsLoading(false)
      return
    }

    let cancelled = false
    setPermissionsLoading(true)

    fetchExpenseClaimPeriodStart()

    fetchAllExpenseUserPermissionsByProcessorUserId({ processorUserId: userId })
      .then((result: any) => {
        if (!cancelled) setPermissions(Array.isArray(result) ? result : [])
      })
      .catch((error: any) => {
        console.error('Error fetching expense user permissions:', error)
        if (!cancelled) setPermissions([])
      })
      .finally(() => {
        if (!cancelled) setPermissionsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [userId, isSubmissionVersion, fetchAllExpenseUserPermissionsByProcessorUserId, fetchExpenseClaimPeriodStart])

  const { disabledStatuses, tooltips: statusTooltips } = useMemo(() => {
    if (isSubmissionVersion) {
      return { disabledStatuses: [] as number[], tooltips: {} as Record<number, string> }
    }

    if (permissionsLoading || selectedRows.length === 0) {
      return getDisabledStatusesFromPermissions([], '')
    }

    const recipientIds = [
      ...new Set(selectedRows.map((row) => row.claimRecipientUserId).filter((id): id is string => !!id))
    ]

    let permResult
    if (permissions.length === 0) {
      const disabledStatuses = [0]
      const tooltips: Record<number, string> = { 0: 'Claims cannot be changed to draft status' }

      if (!submitExpenseClaims?.allowed) {
        ;[1, 2, 3, 4].forEach((s) => {
          disabledStatuses.push(s)
          tooltips[s] = 'You do not have permission to approve claims'
        })
      }

      if (!processExpenseClaims?.allowed) {
        ;[5, 6, 7, 8].forEach((s) => {
          disabledStatuses.push(s)
          tooltips[s] = 'You do not have permission to process payments'
        })
      }

      permResult = { disabledStatuses, tooltips }
    } else {
      permResult = getDisabledStatusesFromPermissions(permissions, recipientIds)
    }

    const currentStatuses = selectedRows
      .map((row) => row.status)
      .filter((s): s is string => s !== undefined && s !== null)
      .map(Number)
    const statusResult = getDisabledStatusesFromCurrentStatuses(currentStatuses)

    return mergeDisabledStatuses(permResult, statusResult)
  }, [permissions, permissionsLoading, selectedRows, isSubmissionVersion, submitExpenseClaims?.allowed, processExpenseClaims?.allowed])

  useEffect(() => {
    if (claimStatus !== null && disabledStatuses.includes(claimStatus)) {
      setClaimStatus(null)
    }
  }, [disabledStatuses, claimStatus])

  const OPERATOR_KEYS = new Set([
    'numberComparisonOperator',
    'dateComparisonOperator',
    'comparisonOperator',
    'operator'
  ])

  const isEmptyPrimitive = (x: unknown) => x === null || x === undefined || (typeof x === 'string' && x.trim() === '')

  const isFilterValueActive = (v: any): boolean => {
    if (isEmptyPrimitive(v)) return false
    if (Array.isArray(v)) return v.some((item) => !isEmptyPrimitive(item))
    switch (typeof v) {
      case 'boolean':
        return true
      case 'number':
        return true
      case 'string':
        return v.trim() !== ''
      case 'object': {
        if ('value' in v) return !isEmptyPrimitive((v as any).value)
        if ('date' in v) return !isEmptyPrimitive((v as any).date)
        return Object.entries(v as Record<string, any>).some(
          ([k, val]) => !OPERATOR_KEYS.has(k) && isFilterValueActive(val)
        )
      }
      default:
        return false
    }
  }

  const areFiltersEffectivelyEmpty = (f: Record<string, any> | null | undefined) => {
    if (!f) return true
    return !Object.values(f).some((v) => isFilterValueActive(v))
  }

  const emptyOutFilters = (f: Record<string, any> | null | undefined) => {
    if (!f) return {}
    const out: Record<string, any> = {}
    for (const [k, v] of Object.entries(f)) {
      if (v && typeof v === 'object') {
        if ('date' in v || 'dateComparisonOperator' in v) {
          out[k] = { date: null, dateComparisonOperator: null }
        } else if ('value' in v || 'numberComparisonOperator' in v) {
          out[k] = { value: null, numberComparisonOperator: null }
        } else {
          out[k] = null
        }
      } else {
        out[k] = null
      }
    }
    return out
  }

  const handleRowSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      setRowSelectionModel(newSelection)

      setSelectedRowsById((prev) => {
        const next: Record<string, ExpenseRow> = {}

        for (const id of newSelection as GridRowId[]) {
          const key = String(id)
          const rowFromGrid = apiRef.current.getRow(id) as ExpenseRow | null
          const row = rowFromGrid ?? prev[key]
          if (row) next[key] = row
        }

        return next
      })
    },
    [apiRef]
  )

  const trayHeight = 72
  const trayOpen = !isSubmissionVersion && selectedRows.length > 0

  const prefsHydratedRef = useRef(false)
  const firstSyncDoneRef = useRef(false)
  const userClearedRef = useRef(false)

  useEffect(() => {
    const prefBag = resolvedConfig?.filters ?? null
    const storeEmpty = areFiltersEffectivelyEmpty(expensesTableFilters?.filters)
    const prefsEmpty = areFiltersEffectivelyEmpty(prefBag)

    if (prefsHydratedRef.current) {
      setFiltersReady(true)
      return
    }

    if (!storeEmpty) {
      prefsHydratedRef.current = true
      setFiltersReady(true)
      return
    }

    if (!prefsEmpty) {
      setExpensesTableFiltersFromPreferences({ filters: prefBag ?? null })
      prefsHydratedRef.current = true
      setFiltersReady(true)
      return
    }

    setFiltersReady(true)
  }, [resolvedConfig?.filters, expensesTableFilters?.filters, setExpensesTableFiltersFromPreferences])

  useEffect(() => {
    setColumnOrder(resolvedConfig.columnOrder ?? fallback.columnOrder ?? [])
    setColumnVisibilityModel(resolvedConfig.columnVisibilityModel ?? fallback.columnVisibilityModel ?? {})
    setColumnWidths(resolvedConfig.columnWidths ?? fallback.columnWidths ?? {})
    setPinnedColumns({
      left: resolvedConfig.columnsPinnedLeft ?? fallback.columnsPinnedLeft ?? [],
      right: resolvedConfig.columnsPinnedRight ?? fallback.columnsPinnedRight ?? []
    })
    setSortModel(resolvedConfig.columnSort ?? fallback.columnSort ?? [{ field: 'createdDate', sort: 'desc' as const }])
  }, [resolvedConfig])

  const useDeepCompareMemoize = (value: any) => {
    const ref = useRef(value)
    const signal = JSON.stringify(value)
    const prevSignal = JSON.stringify(ref.current)
    if (signal !== prevSignal) {
      ref.current = value
    }
    return ref.current
  }

  const deepFilters = useDeepCompareMemoize(expensesTableFilters)

  // Fetch expenses data when filters change
  useEffect(() => {
    if (!filtersReady) return

    const getExpensesData = async () => {
      setLoading(true)

      const filters = deepFilters?.filters || {}

      // Compute the new orderByColumns array from the sort model.
      const computedOrderBy = sortModel
        .map((sortItem) => {
          const fieldMap: Record<string, string> = {
            createdDate: 'incurredDate',
            date: 'incurredDate',
            claimAmount: 'grossAmount'
          }
          const columnName = fieldMap[sortItem.field] ?? sortItem.field
          return { columnName, direction: sortItem.sort === 'asc' ? 0 : 1 }
        })
        .filter(Boolean)

      const orderByColumns = computedOrderBy.length > 0 ? computedOrderBy : null

      const deepFiltersFiltersString = JSON.stringify(deepFilters.filters)
      const currentTableConfigFiltersString = JSON.stringify(resolvedConfig.filters)
      const filtersHaveChanged = deepFiltersFiltersString !== currentTableConfigFiltersString

      const safeToSync = firstSyncDoneRef.current

      if (safeToSync && filtersHaveChanged) {
        updateTableConfigPreference({ filters: deepFilters.filters })
      }

      // Parse associatedMatter filter (format: clientNo-matterNo)
      let associatedClientNo = filters.clientNo || null
      let associatedMatterNo = filters.matterNo || null
      if (filters.associatedMatter) {
        const parts = filters.associatedMatter.split('-')
        if (parts.length >= 2) {
          associatedMatterNo = parts.pop() || null
          associatedClientNo = parts.join('-') || null
        } else {
          associatedClientNo = filters.associatedMatter
        }
      }

      try {
        const response = await fetchAllExtendedExpenses({
          claimRecipientUserIds: filters.claimRecipient || null,
          clientNo: associatedClientNo,
          description: filters.claimDescription || null,
          expenseClaimTypeDescription: filters.claimType || null,
          grossAmountNumberComparisonOperator: filters.claimAmount?.value
            ? (filters.claimAmount?.numberComparisonOperator ?? 0)
            : null,
          grossAmountValue: filters.claimAmount?.value ?? null,
          hasReceipt: filters.receipt === 'Yes' ? true : filters.receipt === 'No' ? false : null,
          incurredDate: filters.dateIncurred?.date || null,
          incurredDateComparisonOperator: filters.dateIncurred?.dateComparisonOperator ?? 0,
          isSubmission: isSubmissionVersion || false,
          isRecoverable: filters.recoverable === 'Yes' ? true : filters.recoverable === 'No' ? false : null,
          matterNo: associatedMatterNo,
          orderByColumns,
          pageSize: deepFilters?.pageSize ?? 25,
          startAtIndex: deepFilters?.startAtIndex ?? 0,
          status:
            filters.status != null
              ? Array.isArray(filters.status)
                ? filters.status.length > 0
                  ? filters.status
                  : null
                : [filters.status]
              : null
        })

        setExpenses(response.rows)
        if (response?.totalRowCount !== undefined) {
          setTotalRowCount(response.totalRowCount)
        }
      } catch (error) {
        console.error('Error fetching expenses:', error)
      } finally {
        setLoading(false)
      }

      if (!firstSyncDoneRef.current) firstSyncDoneRef.current = true
    }

    getExpensesData()
  }, [
    filtersReady,
    deepFilters.pageSize,
    deepFilters.filters,
    deepFilters.startAtIndex,
    sortModel,
    isSubmissionVersion,
    fetchAllExtendedExpenses,
    expensesTableUpdate
  ])

  useEffect(() => {
    if (!expenses || expenses.length === 0) return
    if (isClaimsDrawerOpen) return

    const fetchCommentCounts = async () => {
      try {
        const response = await fetchAllExpensesClaimComments()
        const counts: Record<string, number> = {}
        if (Array.isArray(response)) {
          response.forEach((comment: any) => {
            const id = comment.expenseClaimId
            if (id) counts[id] = (counts[id] || 0) + 1
          })
        }
        setCommentCounts(counts)
      } catch {
        setCommentCounts({})
      }
    }

    fetchCommentCounts()
  }, [expenses, fetchAllExpensesClaimComments, commentsUpdate, isClaimsDrawerOpen])

  const updateTableConfigPreference = useCallback(
    (fieldsToChange: any) => {
      if (!activePrefId) return

      updateUserPreference({
        userConfigurationTypeId: activePrefId,
        value: {
          ...resolvedConfig,
          columnOrder: columnOrder,
          columnVisibilityModel: columnVisibilityModel,
          columnWidths: columnWidths,
          columnsPinnedLeft: pinnedColumns.left,
          columnsPinnedRight: pinnedColumns.right,
          columnSort: sortModel,
          ...fieldsToChange
        },
        convertJsonToString: true,
        categoryName: 'Expenses and Overtime'
      })
    },
    [
      activePrefId,
      resolvedConfig,
      updateUserPreference,
      sortModel,
      columnOrder,
      columnVisibilityModel,
      columnWidths,
      pinnedColumns
    ]
  )

  const onPinnedColumnsChange = useCallback((updatedPinnedColumns: GridPinnedColumnFields) => {
    setPinnedColumns(updatedPinnedColumns)
    updateTableConfigPreference({
      columnsPinnedLeft: updatedPinnedColumns.left,
      columnsPinnedRight: updatedPinnedColumns.right
    })
  }, [])

  const onSortModelChange = (model: GridSortModel) => {
    setSortModel(model)
    updateTableConfigPreference({ columnSort: model })
  }

  const onColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(model)
    updateTableConfigPreference({ columnVisibilityModel: model })
  }

  const onColumnOrderChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const newColumnOrder = columnDefinitions.map((column: any) => column.field)
    setColumnOrder(newColumnOrder)
    updateTableConfigPreference({ columnOrder: newColumnOrder })
  }

  const onColumnWidthChange = () => {
    const columnDefinitions = gridColumnDefinitionsSelector(apiRef)
    const newColumnWidths: Record<string, number> = {}
    columnDefinitions.forEach((column: any) => {
      newColumnWidths[column.field] = column.width
    })
    setColumnWidths(newColumnWidths)
    updateTableConfigPreference({ columnWidths: newColumnWidths })
  }

  const handleClearAll = useCallback(() => {
    userClearedRef.current = true
    resetExpensesTableFilters()
    const emptied = emptyOutFilters(resolvedConfig?.filters ?? expensesTableFilters?.filters ?? {})
    updateTableConfigPreference({ filters: emptied })
  }, [resetExpensesTableFilters, resolvedConfig?.filters, expensesTableFilters?.filters, updateTableConfigPreference])

  const handleNewClaim = useCallback(() => {
    openNewClaimDrawer(true, true) // isExpensesTable = true, openDirectToForm = true
  }, [openNewClaimDrawer])

  const handleSubmitDrafts = useCallback(() => {
    openNewClaimDrawer(true) // isExpensesTable = true
  }, [openNewClaimDrawer])

  const handleExportCsv = useCallback(async () => {
    const filters = expensesTableFilters?.filters || {}

    const computedOrderBy = sortModel
      .map((sortItem) => {
        const fieldMap: Record<string, string> = {
          createdDate: 'incurredDate',
          date: 'incurredDate',
          claimAmount: 'grossAmount'
        }
        const columnName = fieldMap[sortItem.field] ?? sortItem.field
        return { columnName, direction: sortItem.sort === 'asc' ? 0 : 1 }
      })
      .filter(Boolean)

    const orderByColumns = computedOrderBy.length > 0 ? computedOrderBy : null

    let associatedClientNo = filters.clientNo || null
    let associatedMatterNo = filters.matterNo || null
    if (filters.associatedMatter) {
      const parts = filters.associatedMatter.split('-')
      if (parts.length >= 2) {
        associatedMatterNo = parts.pop() || null
        associatedClientNo = parts.join('-') || null
      } else {
        associatedClientNo = filters.associatedMatter
      }
    }

    try {
      await fetchAllExtendedExpensesAsCSV({
        claimRecipientUserIds: filters.claimRecipient || null,
        clientNo: associatedClientNo,
        description: filters.claimDescription || null,
        expenseClaimTypeDescription: filters.claimType || null,
        grossAmountNumberComparisonOperator: filters.claimAmount?.value
          ? (filters.claimAmount?.numberComparisonOperator ?? 0)
          : null,
        grossAmountValue: filters.claimAmount?.value ?? null,
        hasReceipt: filters.receipt === 'Yes' ? true : filters.receipt === 'No' ? false : null,
        incurredDate: filters.dateIncurred?.date || null,
        incurredDateComparisonOperator: filters.dateIncurred?.dateComparisonOperator ?? 0,
        isSubmission: isSubmissionVersion || false,
        isRecoverable: filters.recoverable === 'Yes' ? true : filters.recoverable === 'No' ? false : null,
        matterNo: associatedMatterNo,
        orderByColumns,
        pageSize: expensesTableFilters?.pageSize ?? 25,
        startAtIndex: expensesTableFilters?.startAtIndex ?? 0,
        status:
          filters.status != null
            ? Array.isArray(filters.status)
              ? filters.status.length > 0
                ? filters.status
                : null
              : [filters.status]
            : null
      })
    } catch (error) {
      console.error('Error exporting expenses as CSV:', error)
    }
  }, [expensesTableFilters, sortModel, isSubmissionVersion, fetchAllExtendedExpensesAsCSV])

  const performBulkStatusChange = useCallback(
    async (postingDate?: Date) => {
      const expenseClaimIds = selectedRows
        .map((row) => row.id || row.expenseClaimId)
        .filter((id): id is string => id !== undefined)

      if (expenseClaimIds.length > 0 && claimStatus !== null) {
        setSaving(true)
        try {
          await updateExpenseClaimStatuses({
            status: claimStatus,
            expenseClaimIds,
            // Only sent when moving to "Ready to pay"; applied to all selected claims.
            postingDate:
              claimStatus === 5 && postingDate ? convertDateToLocalTimeInIsoFormat(postingDate).slice(0, 10) : null
          })
        } finally {
          setSaving(false)
        }
      }

      apiRef.current.setRowSelectionModel([])
      setClaimStatus(null)
    },
    [selectedRows, claimStatus, updateExpenseClaimStatuses, apiRef]
  )

  const handleSaveStatusChange = () => {
    // Moving claims to "Ready to pay" (5) requires a posting date, applied to all selected claims.
    if (claimStatus === 5) {
      setPostingDateDialogOpen(true)
      return
    }
    performBulkStatusChange()
  }

  const handleConfirmPostingDate = async (date: Date) => {
    await performBulkStatusChange(date)
    setPostingDateDialogOpen(false)
  }

  const columns: any = [
    {
      field: 'createdDate',
      id: 'createdDate',
      headerName: 'Date incurred',
      type: 'custom',
      width: columnWidths?.['date'] || 150,
      flex: 0.65,
      filterable: true,
      valueGetter: (_value: any, row: any) => row?.incurredDate ?? null,
      renderCell: (params: any) =>
        params.value ? (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            {formatDate(params.value)}
          </MUITypography>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        ),
      renderHeaderFilter: () => <DateDataGridFilter filterType="dateIncurred" isExpensesTable />
    },
    {
      field: 'postingDate',
      id: 'postingDate',
      headerName: 'Posting date',
      type: 'custom',
      width: columnWidths?.['date'] || 150,
      flex: 0.65,
      filterable: false,
      valueGetter: (_value: any, row: any) => row?.postingDate || row?.incurredDate || null,
      renderCell: (params: any) => {
        const requirePostingDateSelection = Boolean(params?.row?.isInClosedPeriod) && Boolean(params?.row?.status < 5)

        return params.value ? (
          <MUITooltip title={requirePostingDateSelection ? `This claim is in a closed period. You must select a posting date in an open period (on or after ${formatDate(expenseClaimPeriodStart) || '-'}).` : ''}>
            <MUIStack display="flex" direction="row" gap={1.5} sx={{ position: 'relative' }}>
              {requirePostingDateSelection ? <WarningIcon sx={{ height: '16px', width: '16px' }} /> : null}
              <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem', color: requirePostingDateSelection ? 'warning.main' : 'inherit' }}>
                {formatDate(params.value)}
              </MUITypography>
            </MUIStack>
          </MUITooltip>
        ) : (
          <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
            -
          </MUITypography>
        )}
    },
    {
      field: 'claimRecipientUserId',
      id: 'claimRecipientUserId',
      headerName: 'Claim recipient',
      type: 'custom',
      width: columnWidths?.['claimRecipientUserId'] || 200,
      flex: 1,
      filterable: !isSubmissionVersion,
      renderHeaderFilter: isSubmissionVersion
        ? () => null
        : () => <ClaimRecipientFilter filterType="claimRecipient" isExpensesTable testId="claim-recipient-filter" />,
      renderCell: (params: any) => {
        return (
          <MUITypography variant="body1" sx={{ mb: 0 }}>
            {params.value || '-'}
          </MUITypography>
        )
      }
    },
    {
      field: 'expenseClaimTypeDescription',
      id: 'expenseClaimTypeDescription',
      headerName: 'Claim type',
      type: 'string',
      width: columnWidths?.['expenseClaimTypeDescription'] || 200,
      flex: 0.65,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <MUITypography variant="body1" sx={{ mb: 0 }}>
            {params.value || '-'}
          </MUITypography>
        )
      }
    },
    {
      field: 'description',
      id: 'description',
      headerName: 'Claim description',
      type: 'string',
      width: columnWidths?.['description'] || 300,
      flex: 1,
      renderHeaderFilter: () => <TextFieldDataGridFilter filterType="claimDescription" isExpensesTable />,
      renderCell: (params: any) => {
        return (
          <MUITypography variant="body1" sx={{ mb: 0 }}>
            {params.value || '-'}
          </MUITypography>
        )
      }
    },
    {
      field: 'associatedMatter',
      id: 'associatedMatter',
      headerName: 'Assoc matter',
      type: 'custom',
      width: columnWidths?.['associatedMatter'] || 150,
      flex: 0.5,
      renderCell: (params: any) => (
        <ClientMatterDetailsPill matterNo={params.row.matterNo} clientNo={params.row.clientNo} sx={{ top: '3px' }} />
      ),
      renderHeaderFilter: () => <TextFieldDataGridFilter filterType="associatedMatter" isExpensesTable />
    },
    {
      field: 'receiptFilename',
      id: 'receiptFilename',
      headerName: 'Receipt',
      type: 'custom',
      width: columnWidths?.['receiptFilename'] || 150,
      flex: 0.5,
      renderCell: (params: any) => (
        <MUIStack display="flex" direction="row" gap={1.5} sx={{ position: 'relative' }}>
          {params.value ? (
            <MUIStack
              display="flex"
              direction="row"
              justifyContent="center"
              alignItems="baseline"
              gap={1.5}
              sx={{ position: 'relative' }}
            >
              <PaperClipIcon sx={{ height: '16px', width: '16px', position: 'relative', top: '4px' }} />
              <MUITypography variant="body2">Attached</MUITypography>
            </MUIStack>
          ) : (
            <MUITypography variant="body2" color="text.secondary">
              -
            </MUITypography>
          )}
        </MUIStack>
      ),
      renderHeaderFilter: () => (
        <SingleSelectDataGridFilter filterType="receipt" label="Select" options={['Yes', 'No']} isExpensesTable />
      )
    },
    {
      field: 'isRecoverable',
      id: 'isRecoverable',
      headerName: 'Recoverable',
      type: 'custom',
      width: columnWidths?.['isRecoverable'] || 200,
      flex: 0.65,
      renderCell: (params: any) => (
        <MUIStack display="flex" direction="row" gap={1.5} sx={{ position: 'relative' }}>
          {params.value ? (
            <Pill
              label="Recoverable"
              color={'#3D517B'}
              backgroundColor={'transparent'}
              sx={{
                height: '25px',
                border: '1px solid #3D517B',
                borderRadius: '16px',
                [`& span`]: { fontSize: '0.75rem' }
              }}
            />
          ) : (
            <MUITypography variant="body2" color="text.secondary">
              -
            </MUITypography>
          )}
        </MUIStack>
      ),
      renderHeaderFilter: () => (
        <SingleSelectDataGridFilter filterType="recoverable" label="Select" options={['Yes', 'No']} isExpensesTable />
      )
    },
    {
      field: 'grossAmount',
      id: 'grossAmount',
      headerName: 'Claim amount',
      type: 'custom',
      width: columnWidths?.['grossAmount'] || 200,
      flex: 0.65,
      renderCell: (params: any) => {
        return (
          <MUITypography variant="body1" sx={{ mb: 0 }}>
            {formatCurrency(params.value || 0) || '-'}
          </MUITypography>
        )
      },
      renderHeaderFilter: () => <BalanceDataGridFilter filterType="claimAmount" isExpensesTable />
    },
    {
      field: 'status',
      id: 'status',
      headerName: 'Status',
      type: 'singleSelect',
      width: columnWidths?.['status'] || 200,
      flex: 0.65,
      renderCell: (params: any) => (
        <MUIStack display="flex" direction="row" gap={1.5} sx={{ position: 'relative' }}>
          {params.value === 0 ? (
            <Pill
              label="DRAFT"
              color={'black'}
              backgroundColor={'#DFE0E4'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : null}
          {params.value === 1 ? (
            <Pill
              label="SUBMITTED"
              color={'black'}
              backgroundColor={'#DFE0E4'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : null}
          {params.value === 2 ? (
            <Pill
              label="APPROVED"
              color={'white'}
              backgroundColor={'#377F86'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : null}
          {params.value === 3 ? (
            <Pill
              label="REJECTED"
              color={'white'}
              backgroundColor={theme.palette.error.main}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : null}
          {params.value === 4 ? (
            <Pill
              label="ACTION REQUIRED"
              color={'white'}
              backgroundColor={'#DE6A1E'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : null}
          {params.value === 5 ? (
            <Pill
              label="READY TO PAY"
              color={'white'}
              backgroundColor={'#5073C3'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : null}
          {params.value === 6 ? (
            <Pill
              label="POSTED"
              color={'white'}
              backgroundColor={'#647495'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : null}
          {params.value === 7 ? (
            <Pill
              label="PAID"
              color={'white'}
              backgroundColor={'#051C33'}
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          ) : null}
        </MUIStack>
      ),
      renderHeaderFilter: () => <ClaimsStatusFilter isExpensesTable isSubmissionVersion={isSubmissionVersion} />
    },
    {
      field: 'comments',
      id: 'comments',
      headerName: '',
      type: 'custom',
      width: columnWidths?.['comments'] || 100,
      flex: 0.35,
      sortable: false,
      hideable: false,
      filterable: false,
      renderCell: (params: any) => {
        const id = params.row.expenseClaimId || params.row.id
        const count = commentCounts[id] || 0
        return (
          <MUIStack display="flex" direction="row" justifyContent="center" alignItems="center">
            {count > 0 ? (
              <>
                <ChatIcon sx={{ height: '18px', width: '18px', position: 'relative', mr: 2 }} />
                <MUITypography variant="body2" sx={{ mb: 0, fontSize: '0.875rem' }}>
                  {count}
                </MUITypography>
              </>
            ) : (
              <MUITypography variant="body2" color="text.secondary" sx={{ mb: 0, fontSize: '0.875rem' }}>
                {' '}
                -
              </MUITypography>
            )}{' '}
          </MUIStack>
        )
      }
    },
    {
      field: 'moreMenu',
      id: 'moreMenu',
      headerName: '',
      type: 'actions',
      width: columnWidths?.['moreMenu'] || 49,
      sortable: false,
      hideable: false,
      filterable: false,
      renderCell: (params: any) => (
        <ClaimsMoreMenuCard isSubmissionVersion={isSubmissionVersion} rowData={params.row} isExpensesTable={true} />
      )
    }
  ]

  const visibleColumns = isSubmissionVersion ? columns.filter((col: any) => col.field !== 'postingDate') : columns

  const sortedColumns =
    columnOrder?.length > 0
      ? [...visibleColumns].sort((a, b) => {
          const targetIndexA = columnOrder.indexOf(a.field)
          const targetIndexB = columnOrder.indexOf(b.field)
          if (targetIndexA === -1 && targetIndexB === -1) return 0
          if (targetIndexA === -1) return 1
          if (targetIndexB === -1) return -1
          return targetIndexA - targetIndexB
        })
      : visibleColumns

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => column.hideable !== false && column.field !== '__check__')
      .map((column) => column.field)
  }

  const paginationModel: GridPaginationModel = {
    page: Math.floor((expensesTableFilters?.startAtIndex ?? 0) / (expensesTableFilters?.pageSize ?? 25)),
    pageSize: expensesTableFilters?.pageSize ?? 25
  }

  useEffect(() => {
    const offset = pageTitleBlockIsVisible ? 310 : 176
    if (windowHeight) setTableHeight(windowHeight - offset)
  }, [windowHeight, pageTitleBlockIsVisible])

  return (
    <MUIPaper
      key={`expenses-table-module`}
      data-testid="ExpensesTableModule"
      sx={{
        position: 'relative',
        fontSize: '0.875rem',
        height: trayOpen ? `calc(${tableHeight}px - ${trayHeight + 16}px)` : `calc(${tableHeight}px - 20px)` || 'auto',
        width: '100%',
        mt: pageTitleBlockIsVisible ? '9rem' : '0rem',
        boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0)',
        [`@media (min-height: 991px)`]: { mt: '-1.5rem' },
        [`& .MuiDataGrid-root`]: {
          border: 'none',
          mb: 0,
          mt: pageTitleBlockIsVisible ? '2rem' : 0,
          [`& div[field="clientNames"]`]: { cursor: 'pointer', [`& .MuiStack-root`]: { cursor: 'pointer' } }
        },
        '& .MuiDataGrid-columnsPanel .MuiDataGrid-panelHeader': {
          display: 'none'
        }
      }}
    >
      {filtersReady && (
        <>
          <TableClearFilters
            filterCount={expensesTableFilterCount}
            onClear={handleClearAll}
            sx={{ position: 'absolute', top: '3px', right: '130px', zIndex: 2 }}
          />
          <DataGridPro
            key={`expenses-grid-${isSubmissionVersion ? 'submission' : 'approval'}`}
            apiRef={apiRef}
            checkboxSelection={!isSubmissionVersion}
            columns={sortedColumns as GridColDef[]}
            columnVisibilityModel={columnVisibilityModel}
            disableRowSelectionOnClick
            disableVirtualization
            getRowId={(row) => row.id || row.expenseClaimId || uuidv4()}
            headerFilters
            loading={loading}
            onCellClick={(params, event) => {
              if (params.field === '__check__' || params.field === 'moreMenu') return
              const expenseId = params.row.id || params.row.expenseClaimId
              if (!expenseId) return
              if (params.field === 'comments') {
                setIsClaimsDrawerOpen(
                  true,
                  { expenseClaimId: expenseId, status: params.row.status },
                  isSubmissionVersion,
                  'chat'
                )
                return
              }
              if (isSubmissionVersion) return
              const navState = { status: params.row.status }
              navigate(`/accounts/employee-claims/expense-claims/expense-review/${expenseId}`, { state: navState })
            }}
            onColumnOrderChange={onColumnOrderChange}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            onColumnWidthChange={onColumnWidthChange}
            onPaginationModelChange={(newModel) => {
              setExpensesTableFilters({
                pageSize: newModel.pageSize,
                startAtIndex: newModel.page * newModel.pageSize
              })
            }}
            onPinnedColumnsChange={onPinnedColumnsChange}
            onRowSelectionModelChange={handleRowSelectionChange}
            onSortModelChange={onSortModelChange}
            pageSizeOptions={[5, 10, 25, 50]}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            pinnedColumns={pinnedColumns}
            rowCount={totalRowCount}
            rows={expenses || []}
            rowSelectionModel={rowSelectionModel}
            slotProps={{
              columnsManagement: { getTogglableColumns },
              loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' }
            }}
            sortModel={sortModel}
            sx={{
              [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
              [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                outline: 'none'
              },
              [`* .${gridClasses.columnHeader}`]: { fontSize: '0.875rem' },
              [`& .MuiDataGrid-cell`]: { alignContent: 'center', [`& .MuiTypography-root`]: { top: 'unset' } },
              [`& .MuiTablePagination-selectLabel`]: {
                display: 'block !important'
              },

              [`& .MuiTablePagination-input`]: {
                display: 'inline-flex !important',
                mb: 0,
                ml: 0,
                mt: '8px',
                fontSize: '0.875rem',
                [`& .MuiTablePagination-select`]: {
                  padding: '0 !important',
                  ml: '4px',
                  mr: '20px',
                  minWidth: 'none',
                  fontSize: '12px',
                  [`&:focus`]: { backgroundColor: 'transparent' }
                },
                [`& .MuiSvgIcon-root`]: { width: '1.5rem', height: '1.5rem', top: 'calc(50% - 12px)' }
              },
              [`& .MuiTablePagination-actions`]: { [`& .MuiButtonBase-root`]: { padding: 0, top: '4px' } },
              [`& .user-restricted-header-name`]: { [`& .MuiDataGrid-columnHeaderTitle`]: { display: 'none' } },
              [`& .MuiDataGrid-row`]: {
                cursor: isSubmissionVersion ? 'default' : 'pointer'
              },
              ...(isSubmissionVersion && {
                [`& .MuiDataGrid-cell[data-field="comments"], & .MuiDataGrid-cell[data-field="moreMenu"]`]: {
                  cursor: 'pointer'
                }
              }),
              [`& .MuiDataGrid-filler`]: {
                display: 'none'
              }
            }}
            slots={{
              columnMenu: (props: GridColumnMenuProps) => (
                <GridColumnMenu
                  {...props}
                  // @ts-ignore
                  sx={{ '.MuiTypography-body1': { mb: 0 } }}
                  slots={{ columnMenuFilterItem: null }}
                />
              ),
              headerFilterMenu: null,
              toolbar: () => (
                <ClaimsToolbarModule
                  showNewClaim={isSubmissionVersion}
                  onNewClaim={handleNewClaim}
                  onSubmitDrafts={handleSubmitDrafts}
                  onExportCsv={handleExportCsv}
                  showManagePermissions={!isSubmissionVersion && setExpenseClaimPermissions?.allowed}
                  onManagePermissions={() => navigate('/accounts/employee-claims/expense-claims/permissions')}
                />
              )
            }}
          />
        </>
      )}
      {!isSubmissionVersion && (
        <DataGridSelectionTray<ExpenseRow> selectedRows={selectedRows} height={trayHeight}>
          {({ selectedRows }) => (
            <>
              <MUITypography variant="body2" sx={{ whiteSpace: 'nowrap', mb: 0 }}>
                Mark selected items as:
              </MUITypography>
              <ClaimStatusDropdownButton
                value={claimStatus}
                onChange={(newStatus) => {
                  setClaimStatus(newStatus)
                }}
                top={true}
                isExpensesTable={true}
                disabledStatuses={disabledStatuses}
                statusTooltips={statusTooltips}
                sx={{ minWidth: 200 }}
              />
              <MUIButton
                disabled={claimStatus === null || saving}
                size="large"
                variant="outlined"
                onClick={handleSaveStatusChange}
                sx={{
                  backgroundColor:
                    claimStatus === null || saving ? theme.palette.grey[300] : theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                  height: 32,
                  minWidth: 100,
                  mr: 4,
                  mb: 0,
                  '&:hover': {
                    backgroundColor:
                      claimStatus === null || saving ? theme.palette.grey[300] : theme.palette.primary.main
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </MUIButton>
            </>
          )}
        </DataGridSelectionTray>
      )}

      <PostingDateDialog
        open={postingDateDialogOpen}
        onClose={() => setPostingDateDialogOpen(false)}
        onConfirm={handleConfirmPostingDate}
        periodStart={expenseClaimPeriodStart}
        isSaving={saving}
        selectedCount={selectedRows.length}
      />
    </MUIPaper>
  )
}

type ExpensesTableModuleProps = {
  isSubmissionVersion?: boolean
}

type ExpenseRow = {
  id?: string
  expenseClaimId?: string
  date?: string
  incurredDate?: string
  claimRecipient?: string
  claimRecipientUserId?: string
  claimType?: string
  expenseClaimTypeDescription?: string
  claimDescription?: string
  description?: string
  matterNo?: string
  clientNo?: string
  receipt?: string
  hasReceipt?: boolean
  recoverable?: string
  isRecoverable?: boolean
  claimAmount?: number
  grossAmount?: number
  netAmount?: number
  vatAmount?: number
  status?: string
}
